/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {CRUDPermissions, DownloadJob, GenericObject} from '@ion/core/src/types/entities';
import {SnackbarMessage} from '@ion/dls/src/components/snackbars/SnackbarMessage';
import useSnackbar from '@ion/dls/src/components/snackbars/useSnackbar';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {DLSTheme} from '@ion/dls/src/types/types';
import {
  cancelDevice,
  cancelDevices,
  initializeDownloadJobs,
  showDevicesStatus
} from '@/store/actions/downloadjobs';
import {
  deviceViewEditingSelector,
  deviceViewErrorSelector,
  deviceViewLoadingSelector,
  deviceViewResultSelector,
  deviceViewSelectedSelector,
  deviceViewSelector,
  deviceViewSpinnerSelector
} from '@/store/selectors/downloadjobs';
import {ExportContext} from '../../../types/redux';
import DeviceCancelPrompt from './DeviceCancelPrompt';
import DevicesStatusGrid from './DevicesStatusGrid';
import DevicesStatusViewDialog from './DevicesStatusViewDialog';
import useDeviceStatusExport from './useDeviceStatusExport';

export interface ViewDownloadJobStatusProps {
  open?: boolean;
  index?: number;
  downloadjobs?: DownloadJob;
  deviceStatus?: Function;
  jobsPermissions?: CRUDPermissions;
  jobStatusDialogClose?: Function;
};

const styles = (theme: DLSTheme) => ({
  panelSection : {
    background : 'white'
  },
  tabStyle : {
    fontFamily    : 'Neo Sans Std',
    fontSize      : '16px',
    fontStyle     : 'normal',
    fontWeight    : 400,
    lineHeight    : '20px',
    letterSpacing : '0px',
    textAlign     : 'center',
    textTransform : 'capitalize'
  },
  contentContainer : {
    width  : '100%',
    height : '75%'
  },
  searchWrapper : {
    border : `1px solid ${theme.dls.colors.grey5}`
  }
});

/**
 * Display View Download Job Status Editor on click on view option from grid menu
 */
export const ViewDownloadJobStatus = ({
  index,
  downloadjobs,
  deviceStatus,
  jobsPermissions,
  jobStatusDialogClose,
  open = false
}: ViewDownloadJobStatusProps) => {
  const editorStyles                  = useExtendStyles(styles);
  const isMounted                     = useHasMounted();
  const [refreshPage, setRefreshPage] = useState(false);
  const gridRefs                      = useRef<GenericObject>();
  const classes                       = editorStyles();
  const dispatch                      = useDispatch();
  const cancelResult                  = useSelector(deviceViewResultSelector);
  const selected                      = useSelector(deviceViewSelector);
  const deviceSelected                = useSelector(deviceViewSelectedSelector);
  const isEditing                     = useSelector(deviceViewEditingSelector);
  const loading                       = useSelector(deviceViewLoadingSelector);
  const isSpinner                     = useSelector(deviceViewSpinnerSelector);
  const error                         = useSelector(deviceViewErrorSelector);

  const {
    pending,
    exportLimit,
    enableExport,
    gridExportCount,
    currentAccountId,
    setTotalRowsCount,
    isAboveExportLimit,
    areTotalRowsSelected,
    isEmailExportRequest,
    setAreTotalRowsSelected,
    setIsEmailExportRequest,
    setSelectedDeviceStatus,
    onCloseEmailExportDialog
  } = useDeviceStatusExport();

  const {
    show: showBulkSnackbar,
    message: bulkSnackbarMessage,
    openSnackbar: onComplete,
    dismissSnackbar: dismissBulkSnackbar
  } = useSnackbar();

  useEffect(() => {
    if (!isMounted) {
      dispatch(initializeDownloadJobs());
    }
  }, [isMounted]);

  useEffect(() => {
    return function() {
      dispatch(cancelDevices(null, null, false));
    };
  }, []);

  const onCancel = useCallback(() => {
    dispatch(showDevicesStatus(downloadjobs, false));
    deviceStatus(false);
  }, [dispatch]);

  const onRefresh = useCallback(() => {
    setRefreshPage(true);
  }, []);

  /**
   * @private
   */
  const onDeleteConfirm = useCallback(function() {
    dispatch(cancelDevice(deviceSelected, selected, index) as any);
  }, [deviceSelected, selected]);

  /**
   * @private
   */
  const onDeleteCancel = useCallback(function() {
    dispatch(cancelDevices(deviceSelected, selected, false));
  }, [deviceSelected, selected]);

  const getDeleteMessage = useCallback(function() {
    if (deviceSelected && !Array.isArray(deviceSelected)) {
      const esn = deviceSelected?.esn;
      return translate('downloadjobs:delete.jobDeleteMessage', {label: esn});
    }
  }, [deviceSelected, selected]);

  /**
   * onRemoveDevice method call when we click on confirm button of Device Cancel Prompt.
   */
  function onRemoveDevice(device) {
    dispatch(cancelDevices(downloadjobs, device));
  };

  return (
    <DevicesStatusViewDialog open={open}
      onCloseDialog={onCancel}
      downloadjobs={downloadjobs}
      index={index}>
      <div className={classes.contentContainer}>
        <DevicesStatusGrid ref={gridRefs}
          onRemoveDevice={onRemoveDevice}
          devicesstatus={downloadjobs}
          jobsPermissions={jobsPermissions}
          refreshPage={refreshPage}
          gridRefresh={loading}
          getAreTotalRowsSelected={setAreTotalRowsSelected}
          areTotalRowsSelected={areTotalRowsSelected}
          setTotalRowsCount={setTotalRowsCount}
          setSelectedDeviceStatus={setSelectedDeviceStatus}
          setIsEmailExportRequest={setIsEmailExportRequest}
          isAboveExportLimit={isAboveExportLimit}
          gridExportCount={gridExportCount}
          isExporting={pending[ExportContext.device] === true}
          exportContext={ExportContext.device}
          enableExport={enableExport}
          exportLimit={exportLimit}
          isEmailExportRequest={isEmailExportRequest}
          onComplete={onComplete}
          onCloseEmailExportDialog={onCloseEmailExportDialog}
          currentAccountId={currentAccountId}
          setRefreshPage={setRefreshPage}
          onRefresh={onRefresh}
          jobStatusDialogClose={jobStatusDialogClose}
          deviceStatusDialogClose={onCancel}
          removeDeviceResult={cancelResult}
          index={index} />
      </div>
      <DeviceCancelPrompt show={isEditing}
        isCancelDevice={true}
        onDeleteConfirm={onDeleteConfirm}
        onDeleteCancel={onDeleteCancel}
        overrideMessage={getDeleteMessage}
        downloadjob={deviceSelected}
        result={cancelResult}
        loading={isSpinner}
        error={error} />
      <SnackbarMessage show={showBulkSnackbar}
        message={bulkSnackbarMessage}
        onDismiss={dismissBulkSnackbar} />
    </DevicesStatusViewDialog>
  );
};

export default ViewDownloadJobStatus;
